import AuthService from '@/services/authService.ts';
import { computed } from 'vue';
import { useSignStore } from '@/stores/sign.ts';
import { useRoute } from 'vue-router';
import { SignStatus } from '@/types/enums/SignStatus';
import { DocumentStatus } from '@/types/enums/DocumentStatus';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import { SignatureType } from '@/types/enums/SignatureType';

export function useSignContext() {
  const route = useRoute();
  const signStore = useSignStore();

  const isGuest = computed(() => {
    return !AuthService.isAuthenticated;
  });

  const signKey = computed(() => {
    if (signStore.document?.signContext.signKey) {
      return signStore.document.signContext.signKey;
    } else {
      return Array.isArray(route.params.id)
        ? route.params.id[0]
        : route.params.id;
    }
  });

  const signatureType = computed(
    () => signStore.document?.signContext.signatureType
  );
  const signStatus = computed(
    () => signStore.document?.signContext?.signStatus
  );

  const userEmail = computed(() => {
    if (isGuest.value) {
      return signStore.userEmail;
    } else {
      return AuthService.user?.email;
    }
  });

  // Here we check if all the signees are the one that is logged in
  const userSignees = computed(() => {
    return signStore.document?.signeesOrdered
      .flat()
      .filter((signee) => signee.email === userEmail.value);
  });

  const userStatusPage = computed(() => {
    const isRequestorPending =
      userSignees.value &&
      userSignees.value.some(
        (signee) =>
          (signee.signeeType === SigneeType.SIGN &&
            signee.signStatus === SignStatus.PENDING) ||
          (signee.signeeType === SigneeType.APPROVE &&
            signee.signStatus === SignStatus.PENDING)
      );

    if (
      signatureType.value === 'seal' &&
      signStatus.value === SignStatus.PENDING
    ) {
      return 'document-seal';
    } else if (
      signatureType.value === 'seal' &&
      signStatus.value === SignStatus.SIGNED
    ) {
      return 'document-details';
    } else if (isGuest.value || route.name === 'sign-id') {
      return 'sign-id';
    } else if (
      (userSignees.value &&
        userSignees.value.length &&
        userSignees.value[0].signStatus !== SignStatus.SIGNED &&
        userSignees.value[0].signStatus !== SignStatus.APPROVED &&
        signStore.document?.documentStatus === DocumentStatus.IN_PROGRESS) ||
      (userSignees.value &&
        userSignees.value.length &&
        isRequestorPending &&
        signStore.document?.documentStatus === DocumentStatus.IN_PROGRESS)
    ) {
      return 'document-sign';
    } else if (
      (userSignees.value && userSignees.value.length) ||
      signStore.document?.documentStatus === DocumentStatus.WITHDRAWN
    ) {
      return 'document-details';
    } else {
      return null;
    }
  });

  const policy = computed(() => {
    return signStore.document?.documentPolicy || {};
  });

  const signeeId = computed(() => {
    return signStore.document?.signContext.signeeId || undefined;
  });

  const requestorEmail = computed(() => {
    return signStore.document?.initiatorDisplayEmail || undefined;
  });

  const isRequestor = computed(() => {
    return (
      requestorEmail.value?.toLowerCase() === userEmail.value?.toLowerCase()
    );
  });

  const jurisdiction = computed(() => {
    return signStore.document?.jurisdiction;
  });

  const isSignatureTypeSeal = computed(() => {
    return signStore.document?.signContext.signatureType === SignatureType.SEAL;
  });

  const isSignatureType = computed(() => {
    return (
      signStore.document?.signContext.signatureType === SignatureType.SIGNATURE
    );
  });

  const availableSeals = computed(() => {
    if (signStore.document && signStore.document?.signContext.availableSeals) {
      return signStore.document.signContext.availableSeals;
    } else {
      return [];
    }
  });

  const signatureMode = computed(() => {
    return signStore.document?.signatureMode;
  });

  return {
    isGuest,
    isRequestor,
    isSignatureTypeSeal,
    isSignatureType,
    jurisdiction,
    policy,
    requestorEmail,
    signKey,
    signeeId,
    userEmail,
    userSignees,
    userStatusPage,
    availableSeals,
    signatureType,
    signStatus,
    signatureMode
  };
}
